import React, { useMemo } from "react";
import Highcharts from "highcharts/highmaps";
import HighchartsReact from "highcharts-react-official";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import CurrencySymbol from "../../../Components/common/CurrencySymbol";
import WidgetShell from "./WidgetShell";
import { HC_CONTAINER_PROPS } from "./chartTheme";
import { EXD, ExdLegendRow, ExdStatItem, ExdStatLine } from "./exdMui";

const COLLECTION_COLORS = {
  collected: "#16a36a",
  outstanding: "#e0457b",
  advance: "#c9a400",
};

const normalizeLabel = (name = "") => {
  const n = String(name).toLowerCase();
  if (n.includes("collect") || n.includes("paid")) return "Collected";
  if (n.includes("outstanding") || n.includes("overdue")) return "Outstanding";
  if (n.includes("advance") || n.includes("credit")) return "Advance payment";
  return name;
};

const colorFor = (label) => {
  const n = String(label).toLowerCase();
  if (n.includes("collect")) return COLLECTION_COLORS.collected;
  if (n.includes("outstanding")) return COLLECTION_COLORS.outstanding;
  if (n.includes("advance")) return COLLECTION_COLORS.advance;
  return "#7a8095";
};

const CollectionsOutstandingWidget = ({
  loading,
  translate,
  chartOptions,
  totalValue,
  loginUserCurrency,
  infoTitle,
}) => {
  const points = useMemo(() => {
    const raw = chartOptions?.series?.[0]?.data;
    if (!Array.isArray(raw)) return [];
    const order = { collected: 0, outstanding: 1, advance: 2 };
    return raw
      .map((point, index) => {
        const name =
          point && typeof point === "object"
            ? normalizeLabel(point.name || `Item ${index + 1}`)
            : `Item ${index + 1}`;
        const y =
          point && typeof point === "object"
            ? Number(point.y) || 0
            : Number(point) || 0;
        return { name, y, color: colorFor(name) };
      })
      .filter((p) => p.name !== "totalOverDue")
      .sort((a, b) => {
        const ak = Object.keys(order).find((k) =>
          String(a.name).toLowerCase().includes(k),
        );
        const bk = Object.keys(order).find((k) =>
          String(b.name).toLowerCase().includes(k),
        );
        return (order[ak] ?? 99) - (order[bk] ?? 99);
      });
  }, [chartOptions]);

  const total = useMemo(
    () =>
      Number(totalValue) ||
      points.reduce((sum, p) => sum + (Number(p.y) || 0), 0),
    [points, totalValue],
  );

  const collectedPct = useMemo(() => {
    const collected = points.find((p) =>
      String(p.name).toLowerCase().includes("collect"),
    );
    if (!collected || !total) return 0;
    return (collected.y / total) * 100;
  }, [points, total]);

  const ringOptions = useMemo(() => {
    if (!points.length) return null;
    const hasValue = points.some((p) => p.y > 0);
    const data = hasValue
      ? points
      : points.map((p) => ({ ...p, y: 1, color: "#e3e5ee" }));

    return {
      chart: {
        type: "pie",
        height: 150,
        width: 150,
        backgroundColor: "transparent",
        spacing: [0, 0, 0, 0],
        margin: [0, 0, 0, 0],
        animation: false,
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        enabled: hasValue,
        backgroundColor: "#161a3a",
        borderColor: "#161a3a",
        borderRadius: 8,
        outside: true,
        headerFormat: "",
        style: { color: "#fff", fontSize: "12px" },
        pointFormatter() {
          return `<b>${this.name}</b><br/>${nFormatter(this.y, 2)}`;
        },
        useHTML: true,
      },
      plotOptions: {
        pie: {
          innerSize: "72%",
          size: "100%",
          borderWidth: 3,
          borderColor: "#ffffff",
          dataLabels: { enabled: false },
          enableMouseTracking: hasValue,
          animation: false,
        },
      },
      series: [{ type: "pie", data }],
    };
  }, [points]);

  return (
    <WidgetShell
      title={translate("Collections & Outstanding")}
      infoTitle={infoTitle}
      loading={loading}
      summary={
        <ExdStatLine>
          <ExdStatItem label="Total billed">
            <Tooltip
              title={
                <Typography sx={{ fontSize: 14 }}>
                  {total?.toLocaleString?.() || total || 0}
                </Typography>
              }
              placement="bottom-start"
              enterDelay={300}
            >
              <Box component="span">
                {total ? nFormatter(total, 2) : 0}{" "}
                <CurrencySymbol
                  currency={loginUserCurrency}
                  withParentheses={false}
                />
              </Box>
            </Tooltip>
          </ExdStatItem>
        </ExdStatLine>
      }
    >
      <Stack alignItems="center" spacing={1.75} sx={{ width: "100%" }}>
        <Box
          sx={{
            flex: "none",
            height: 150,
            overflow: "visible",
            position: "relative",
            width: 150,
          }}
        >
          {ringOptions ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={ringOptions}
              containerProps={HC_CONTAINER_PROPS}
            />
          ) : null}
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ inset: "28px", pointerEvents: "none", position: "absolute" }}
          >
            <Typography
              component="b"
              sx={{
                color: EXD.text,
                fontFamily: EXD.font,
                fontSize: 20,
                fontVariantNumeric: "tabular-nums",
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              {collectedPct.toFixed(1)}%
            </Typography>
            <Typography sx={{ color: EXD.muted, fontSize: 11, fontWeight: 600 }}>
              collected
            </Typography>
          </Stack>
        </Box>

        <Stack spacing={1} sx={{ width: "100%" }}>
          {points.map((point, index) => {
            const pct = total ? (point.y / total) * 100 : 0;
            return (
              <ExdLegendRow
                key={`${point.name}-${index}`}
                color={point.color}
                label={point.name}
                value={`${nFormatter(point.y, 2)} · ${pct.toFixed(1)}%`}
                round
              />
            );
          })}
        </Stack>
      </Stack>
    </WidgetShell>
  );
};

export default CollectionsOutstandingWidget;
